import Accordion from '@react/shared/components/Accordion';
import BreadCrumb from '@react/shared/components/BreadCrumb';
import HeaderContainer from '@react/shared/components/Header/Header.container';
import { tocsMap } from '@sly/core/constants/filters';
import { populate } from '@sly/core/constants/populate';
import Avatar from '@sly/frontend/react/shared/components/Avatar';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import clsxm from 'sly/lib/clsxm';
import { getPartnerData } from 'sly/provider/helpers';
import { usePrefetch } from 'sly/services/api';
import { useEvents } from 'sly/services/events';
import { Link } from 'sly/system';

import ExploreCommunities from '../ExploreCommunities';
import PartnerPricingTable from '../ProviderPricingTable';
import PartnerReviewsSection from '../ProviderReviewsSection';
import ProviderSeo from '../ProviderSeo';

const ProviderPageSection = ({ twoColumn, children }) => {
  return (
    <>
      <div
        className='lg:layout mx-m py-12 md:mx-l lg:mx-auto'
        data-tp-id='gen-div-8cccff20-9cc6-44e2-8dd9-678e4499df12'
      >
        <section
          className={clsxm(
            twoColumn && 'gap-[0_1.5rem] lg:grid lg:grid-cols-[42.5rem_auto]'
          )}
        >
          {children}
        </section>
      </div>
    </>
  );
};

export default function ProviderPage() {
  const { slug } = useParams() || {};
  const { events } = useEvents();
  const {
    requestInfo: { response },
  } = usePrefetch('getProviderPage', {
    'filters[providerSlug][$eq]': `${slug}`,
    populate: populate.providerPage,
  });

  const {
    requestInfo: { normalized: partnerCollection },
  } = usePrefetch('getCommunityPartner', {
    id: slug,
  });

  const getMemoizedCallback = useCallback(
    (partnerCollection, communityName) =>
      getPartnerData(partnerCollection, communityName),
    []
  );
  const parsePartnerDataFromApiAndCms = useMemo(
    () => getMemoizedCallback(partnerCollection),
    []
  );

  if (!response?.data?.length) {
    return;
  }

  const [cmsData] = response?.data || [];

  const {
    description,
    highlights,
    typeOfCares,
    priceRangeTOC,
    faqs,
    providerName,
    similarProviderNearby,
    popularCommunities,
    isPricingTableVisible,
    logo,
  } = parsePartnerDataFromApiAndCms(cmsData);

  const tocsWithLink = typeOfCares.map((toc) => {
    const { label, href } = tocsMap?.[toc] || {};
    return {
      label,
      href,
    };
  });

  return (
    <>
      <ProviderSeo partnerCollection={partnerCollection} />
      <HeaderContainer />
      <main>
        <ProviderPageSection twoColumn>
          <div data-tp-id='gen-div-f4e43fcb-46be-4961-aa94-b5cd6fea8ee2'>
            <BreadCrumb
              items={[
                { label: 'Home', path: '/' },
                { label: 'Providers', path: '/providers' },
                { label: providerName },
              ]}
            />
            {logo && (
              <div className='my-3 h-24 w-24 md:h-36 md:w-36'>
                <Avatar path={logo.path} />
              </div>
            )}
            <h1
              className='font-t-xl mb-6'
              data-tp-id='gen-h1-40122e68-8cb0-43d5-bd62-488a9f47f68c'
            >
              {providerName}
            </h1>
            {!!description && (
              <div
                data-tp-id='gen-div-1efb00dd-d377-4ef1-bfe0-c184348e6ca0'
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>

          <div
            className='mt-6 bg-slate-lighter-95 p-6 lg:mt-0'
            data-tp-id='gen-div-fe07b25f-53b7-43de-9cf3-90916a3d4269'
          >
            <h3
              className='font-t-s'
              data-tp-id='gen-h3-6c19ef1e-26e9-404c-be93-6ab5fb847558'
            >
              Overview
            </h3>
            <div
              className='mt-4 flex flex-col flex-wrap gap-4 md:flex-row md:child:w-[45%] lg:flex-col lg:child:w-full'
              data-tp-id='gen-div-e487d19e-e0b0-4048-b7d9-be5d60310086'
            >
              {highlights.map(({ text, Icon }) => {
                return (
                  <div
                    key={text}
                    className='flex items-center gap-2'
                    data-tp-id='gen-div-d569b1ef-2d67-4b15-80bf-aa6b2b971cf5'
                  >
                    <Icon />
                    <span data-tp-id='gen-span-509b48a5-7110-484e-a0e5-462ffa0f765f'>
                      {text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </ProviderPageSection>
        <hr />
        <ProviderPageSection>
          <div data-tp-id='gen-div-a0e8985d-e9d1-42cc-bc3f-6ccf43dcd0f9'>
            <h2
              className='font-t-l'
              data-tp-id='gen-h2-96871006-3280-4276-9a60-8a99eee136a4'
            >
              Explore {`${providerName}'s`} community listings
            </h2>
            <ExploreCommunities partnerCollection={partnerCollection} />
          </div>
        </ProviderPageSection>
        <hr />
        <ProviderPageSection twoColumn>
          <div data-tp-id='gen-div-6c043521-facb-4239-9fb7-44059b4c19a0'>
            {!!tocsWithLink?.length && (
              <>
                <div data-tp-id='gen-div-79087f6f-a077-4b36-be7a-4deb0dd896fd'>
                  <h2
                    className='font-t-l mb-6'
                    data-tp-id='gen-h2-d721701a-4af1-435a-88c0-c79da2aeddd9'
                  >
                    Types of Care Provided by {providerName}
                  </h2>
                  <div
                    className='flex flex-col gap-4'
                    data-tp-id='gen-div-03546e28-c785-48c5-9ee6-eae818398a68'
                  >
                    {tocsWithLink?.map(({ label, href }) => {
                      return (
                        <Link
                          className='block'
                          key={`${label}${href}`}
                          underlined
                          href={href}
                          data-tp-id='gen-Link-0ccc26e9-2663-4207-b2a1-ea9b0026b7a5'
                        >
                          {label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <hr className='my-12' />
              </>
            )}
            {!!popularCommunities?.links?.length && (
              <>
                <div data-tp-id='gen-div-ef083b28-a5bd-49aa-84cc-e0d3b20c2407'>
                  <h2
                    className='font-t-l mb-6'
                    data-tp-id='gen-h2-31bb66e9-7ed6-43e7-83da-36a6a007f5a7'
                  >
                    {popularCommunities?.title}
                  </h2>
                  <div
                    className='flex flex-col gap-4'
                    data-tp-id='gen-div-f5220fa2-52f0-4cfd-93ae-ed5e087cb031'
                  >
                    {popularCommunities?.links?.map(({ text, url }) => {
                      return (
                        <Link
                          className='block'
                          key={`${text}${url}`}
                          underlined
                          href={url}
                          data-tp-id='gen-Link-3facd5ae-8a07-4586-b0d8-31dc693d60c3'
                        >
                          {text}
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <hr className='my-12' />
              </>
            )}

            {isPricingTableVisible && (
              <>
                <div data-tp-id='gen-div-223ed524-f820-46bc-af3f-b4da920c6e25'>
                  <h2
                    className='font-t-l mb-6'
                    data-tp-id='gen-h2-2fa3408b-fc9b-4b59-a624-330703eb24b6'
                  >
                    Estimated {providerName} price ranges:
                  </h2>
                  <p>
                    {providerName} pricing will vary widely from community to
                    community, from city to city, and is also highly dependent
                    on the type of care an older adult needs.
                  </p>
                  <PartnerPricingTable
                    priceRangeTOC={priceRangeTOC}
                    className='mt-6'
                  />
                </div>
                <hr className='my-12' />
              </>
            )}

            {!!faqs?.length && (
              <>
                <div data-tp-id='gen-div-20a8ef83-9f18-4fb5-8c5f-1fba8b3efd25'>
                  <h2
                    className='font-t-l'
                    data-tp-id='gen-h2-a82c2b62-5435-47eb-80c5-56028ae38899'
                  >
                    Frequently asked questions about {providerName}
                  </h2>
                  <div
                    className='mt-6'
                    data-tp-id='gen-div-6a2978e2-cd2e-4f34-bd91-460b4a38662e'
                  >
                    {faqs.map((faq, index) => {
                      return (
                        <>
                          <Accordion
                            key={index}
                            title={faq.title}
                            content={faq.description}
                            parseRaw
                          />
                          {index < faqs.length - 1 && (
                            <hr className='m-0 mb-l mt-l block border-0 border-t md:mb-xl' />
                          )}
                        </>
                      );
                    })}
                  </div>
                </div>
                <hr className='my-12' />
              </>
            )}

            <div data-tp-id='gen-div-ee9906ed-b3c6-4971-be0d-d9de1bb9820e'>
              <PartnerReviewsSection partnerCollection={partnerCollection} />
            </div>

            {!!similarProviderNearby?.links?.length && (
              <>
                <div data-tp-id='gen-div-1ec5ba8e-ea26-4e4b-abaa-0a3e5357311e'>
                  <h2
                    className='font-t-l mb-6'
                    data-tp-id='gen-h2-81e8bc69-92df-460b-bad1-c7ef37fc9615'
                  >
                    {similarProviderNearby?.title}
                  </h2>
                  <div
                    className='flex flex-col gap-4'
                    data-tp-id='gen-div-d3818d31-fff4-493f-a574-5493745e0426'
                  >
                    {similarProviderNearby?.links?.map(({ text, url }) => {
                      return (
                        <Link
                          className='block'
                          key={`${text}${url}`}
                          underlined
                          to={url}
                          data-tp-id='gen-Link-552427de-fdec-4e24-84ca-ecaa7107b65c'
                        >
                          {text}
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <hr className='my-12' />
              </>
            )}
          </div>
        </ProviderPageSection>
      </main>
    </>
  );
}
